import { diffLines, diffWordsWithSpace } from 'diff';
import { blocksToMarkdown } from '../content/markdown.js';
import { flattenSections } from '../content/numbering.js';
import { richToText } from '../content/text.js';
import type { DocumentContent, Section } from '../content/types.js';

export type ChangeKind = 'added' | 'removed' | 'modified' | 'moved' | 'renamed' | 'unchanged';

export interface TextChange {
  type: 'add' | 'remove' | 'equal';
  value: string;
}

export interface LineHunk {
  type: 'add' | 'remove' | 'equal';
  lines: string[];
  oldStart: number;
  newStart: number;
}

export interface SectionDiff {
  kind: ChangeKind;
  anchor: string;
  sectionId: string;
  title: string;
  previousTitle: string | null;
  /** Clause number in the base version, if present. */
  previousNumber: string | null;
  /** Clause number in the compared version, if present. */
  number: string | null;
  /** Word-level changes for the section body. */
  changes: TextChange[];
  previousText: string;
  currentText: string;
  addedWords: number;
  removedWords: number;
}

export interface DocumentDiff {
  sections: SectionDiff[];
  summary: {
    sectionsAdded: number;
    sectionsRemoved: number;
    sectionsModified: number;
    sectionsMoved: number;
    sectionsRenamed: number;
    wordsAdded: number;
    wordsRemoved: number;
    /** 0-1, share of the base document that is unchanged. */
    similarity: number;
  };
}

function countWords(text: string): number {
  const m = text.match(/[\p{L}\p{N}][\p{L}\p{N}'’-]*/gu);
  return m ? m.length : 0;
}

function sectionText(section: Section): string {
  return blocksToMarkdown(section.body.content).trim();
}

interface Indexed {
  section: Section;
  number: string;
  order: number;
  path: string;
}

function indexSections(content: DocumentContent): Map<string, Indexed> {
  const map = new Map<string, Indexed>();
  flattenSections(content, { includeUnnumbered: true }).forEach((entry, order) => {
    map.set(entry.section.id, { section: entry.section, number: entry.number, order, path: entry.path.join(' › ') });
  });
  return map;
}

/**
 * Structural diff between two versions of a document.
 *
 * Sections are matched by their stable id first, then by anchor, so renames and
 * reorders are reported as such rather than as an add plus a remove.
 */
export function diffContent(base: DocumentContent, next: DocumentContent): DocumentDiff {
  const baseById = indexSections(base);
  const nextById = indexSections(next);
  const baseByAnchor = new Map<string, Indexed>();
  for (const entry of baseById.values()) if (!baseByAnchor.has(entry.section.anchor)) baseByAnchor.set(entry.section.anchor, entry);

  const consumed = new Set<string>();
  const sections: SectionDiff[] = [];
  let wordsAdded = 0;
  let wordsRemoved = 0;
  let unchangedWords = 0;
  let baseWords = 0;

  const buildDiff = (before: Indexed | null, after: Indexed | null): SectionDiff => {
    const previousText = before ? sectionText(before.section) : '';
    const currentText = after ? sectionText(after.section) : '';
    const changes: TextChange[] =
      previousText === currentText
        ? currentText
          ? [{ type: 'equal', value: currentText }]
          : []
        : diffWordsWithSpace(previousText, currentText).map((part) => ({
            type: part.added ? ('add' as const) : part.removed ? ('remove' as const) : ('equal' as const),
            value: part.value,
          }));

    let added = 0;
    let removed = 0;
    let same = 0;
    for (const change of changes) {
      const n = countWords(change.value);
      if (change.type === 'add') added += n;
      else if (change.type === 'remove') removed += n;
      else same += n;
    }

    let kind: ChangeKind;
    if (!before) kind = 'added';
    else if (!after) kind = 'removed';
    else if (previousText !== currentText) kind = 'modified';
    else if (before.section.title !== after.section.title) kind = 'renamed';
    else if (before.number !== after.number || before.order !== after.order) kind = 'moved';
    else kind = 'unchanged';

    wordsAdded += added;
    wordsRemoved += removed;
    unchangedWords += same;
    baseWords += same + removed;

    const target = (after ?? before)!;
    return {
      kind,
      anchor: target.section.anchor,
      sectionId: target.section.id,
      title: target.section.title,
      previousTitle: before && after && before.section.title !== after.section.title ? before.section.title : null,
      previousNumber: before ? before.number || null : null,
      number: after ? after.number || null : null,
      changes,
      previousText,
      currentText,
      addedWords: added,
      removedWords: removed,
    };
  };

  // Walk the new version in reading order so the diff reads like the document.
  for (const entry of [...nextById.values()].sort((a, b) => a.order - b.order)) {
    let before = baseById.get(entry.section.id) ?? null;
    if (!before) {
      const byAnchor = baseByAnchor.get(entry.section.anchor);
      if (byAnchor && !consumed.has(byAnchor.section.id)) before = byAnchor;
    }
    if (before) consumed.add(before.section.id);
    sections.push(buildDiff(before, entry));
  }

  // Anything left in the base was removed.
  for (const entry of [...baseById.values()].sort((a, b) => a.order - b.order)) {
    if (consumed.has(entry.section.id)) continue;
    sections.push(buildDiff(entry, null));
  }

  const summary = {
    sectionsAdded: sections.filter((s) => s.kind === 'added').length,
    sectionsRemoved: sections.filter((s) => s.kind === 'removed').length,
    sectionsModified: sections.filter((s) => s.kind === 'modified').length,
    sectionsMoved: sections.filter((s) => s.kind === 'moved').length,
    sectionsRenamed: sections.filter((s) => s.kind === 'renamed').length,
    wordsAdded,
    wordsRemoved,
    similarity: baseWords === 0 ? (wordsAdded === 0 ? 1 : 0) : Number((unchangedWords / baseWords).toFixed(4)),
  };

  return { sections, summary };
}

/** Line-oriented unified diff of two Markdown renderings, for a raw side-by-side view. */
export function diffMarkdown(before: string, after: string): LineHunk[] {
  const parts = diffLines(before, after);
  const hunks: LineHunk[] = [];
  let oldLine = 1;
  let newLine = 1;
  for (const part of parts) {
    const lines = part.value.split('\n');
    if (lines.length && lines[lines.length - 1] === '') lines.pop();
    const type: LineHunk['type'] = part.added ? 'add' : part.removed ? 'remove' : 'equal';
    hunks.push({ type, lines, oldStart: oldLine, newStart: newLine });
    if (type !== 'add') oldLine += lines.length;
    if (type !== 'remove') newLine += lines.length;
  }
  return hunks;
}

/** Inline word diff of two plain strings (used for titles and metadata fields). */
export function diffText(before: string, after: string): TextChange[] {
  return diffWordsWithSpace(before, after).map((part) => ({
    type: part.added ? ('add' as const) : part.removed ? ('remove' as const) : ('equal' as const),
    value: part.value,
  }));
}

/** Short human summary such as "3 sections changed, +120/-45 words". */
export function describeDiff(diff: DocumentDiff): string {
  const { summary } = diff;
  const changed =
    summary.sectionsAdded + summary.sectionsRemoved + summary.sectionsModified + summary.sectionsRenamed + summary.sectionsMoved;
  if (changed === 0) return 'No changes';
  const bits: string[] = [];
  if (summary.sectionsAdded) bits.push(`${summary.sectionsAdded} added`);
  if (summary.sectionsRemoved) bits.push(`${summary.sectionsRemoved} removed`);
  if (summary.sectionsModified) bits.push(`${summary.sectionsModified} edited`);
  if (summary.sectionsRenamed) bits.push(`${summary.sectionsRenamed} renamed`);
  if (summary.sectionsMoved) bits.push(`${summary.sectionsMoved} moved`);
  return `${bits.join(', ')} · +${summary.wordsAdded}/-${summary.wordsRemoved} words`;
}

export { richToText };
